import { Input, InputProps, Select, SelectProps } from "antd";
import classNames from "classnames";
import { FC, ChangeEvent, HTMLAttributes, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { COUNTRIES_NUMBERCODES } from "src/Seed/Countries/Countries";

type PhonenumberInputType = {
  value?: string;
  initialDialCode?: string;
  initialNumber?: string;
  onChange?: (val: string) => void;
  selectProps?: SelectProps;
  inputProps?: InputProps;
} & Omit<HTMLAttributes<HTMLDivElement>, "onChange">;

const COUNTRY_OPTIONS = COUNTRIES_NUMBERCODES.map((country, index) => ({
  ...country,
  key: `${country.value}-${index}`,
}));

const SORTED_DIAL_CODES = [...COUNTRIES_NUMBERCODES].sort((a, b) => b.value.length - a.value.length);

const splitPhoneValue = (value?: string) => {
  if (!value) return { dialCode: undefined, number: "" };
  const match = SORTED_DIAL_CODES.find((country) => value.startsWith(country.value));
  return match ? { dialCode: match.value, number: value.slice(match.value.length) } : { dialCode: undefined, number: value };
};

const PhonenumberInput: FC<PhonenumberInputType> = ({
  value,
  initialDialCode,
  initialNumber,
  onChange,
  className,
  selectProps,
  inputProps,
  ...props
}) => {
  const { t } = useTranslation();
  const [dialCode, setDialCode] = useState<string | undefined>(() =>
    value !== undefined ? splitPhoneValue(value).dialCode : initialDialCode,
  );
  const [number, setNumber] = useState(() => (value !== undefined ? splitPhoneValue(value).number : (initialNumber ?? "")));
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value === undefined || value === lastEmitted.current) return;
    const next = splitPhoneValue(value);
    setDialCode(next.dialCode);
    setNumber(next.number);
    lastEmitted.current = value;
  }, [value]);

  const emit = (nextDialCode: string | undefined, nextNumber: string) => {
    const composed = `${nextDialCode ?? ""}${nextNumber}`;
    lastEmitted.current = composed;
    onChange?.(composed);
  };

  const handleKeydown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key.length === 1 && !/[0-9]/.test(e.key) && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }
  };

  const handleNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const nextNumber = e.target.value.replace(/\D/g, "");
    setNumber(nextNumber);
    emit(dialCode, nextNumber);
  };

  const handleCountryChange = (nextDialCode: string) => {
    setDialCode(nextDialCode);
    emit(nextDialCode, number);
  };

  return (
    <div
      className={classNames(
        "border-border-default flex h-[52px] items-stretch rounded-md border bg-background-default transition-colors overflow-hidden!",
        "hover:border-border-active focus-within:border-border-active focus-within:shadow-[0_0_0_2px_rgba(42,125,225,0.12)]",
        className,
      )}
      {...props}
    >
      <Select
        options={COUNTRY_OPTIONS}
        value={dialCode}
        onChange={handleCountryChange}
        showSearch={{ optionFilterProp: "label" }}
        popupMatchSelectWidth={false}
        variant="borderless"
        className="w-[104px]! rounded-none! [&_.ant-select-selector]:h-full! [&_.ant-select-selector]:px-3! shrink-0 [&_.ant-select-selector]:items-center"
        {...selectProps}
      />
      <Input
        className="h-full! rounded-none! border-none! px-3! shadow-none! flex-1"
        placeholder={t("Common.phoneNumberPlaceholder")}
        inputMode="numeric"
        onKeyDown={handleKeydown}
        onChange={handleNumberChange}
        value={number}
        {...inputProps}
      />
    </div>
  );
};

export default PhonenumberInput;
